import { toast } from "@indigo-components";
import { useAtomValue, useSetAtom } from "jotai";
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type ReferencePdf,
  referencePdfOpenRequestAtom,
  referencePdfViewerStatusAtom,
} from "~/atoms/referencePdf";
import { getConfig } from "~/config/configProvider";
import { useServices } from "~/hooks/useServices";
import type { PDFSearchResult } from "~/indigo-components/src/components/InlinePDFViewer/PDFSearchBar";
import { searchGeneralDocsByFilename } from "./generalDocsSearch";
import { ReferencePdfFloatingViewer } from "./ReferencePdfFloatingViewer";
import { deriveReferencePdfOpenState } from "./referencePdfOpenState";

// Module-scoped download promises so each static PDF is fetched at most once
// per page load and survives the viewer closing/reopening. A failure (or an
// empty blob) drops its entry so a later click retries instead of erroring
// forever. Caveat: an S3 object replaced mid-session stays stale until reload —
// acceptable for these static reference docs.
const pdfPromises = new Map<string, Promise<Blob | null>>();

interface ActiveViewer {
  pdf: ReferencePdf;
  blob: Blob;
  initialPage: number;
  initialSearchQuery?: string;
  initialSearchText?: string;
  initialHighlightTerms?: string[];
  requestToken: number;
  anchorRef?: RefObject<HTMLDivElement | null>;
}

/**
 * The one reference-PDF viewer in the app, mounted by `App` so it
 * outlives sidebars and route changes. Every opener — chatbot AMA links, the
 * IR result list's Table/Figure links, the IR sidebar's Learn More buttons —
 * writes `referencePdfOpenRequestAtom`; an open viewer is reused (new page,
 * search or PDF) rather than a second one opened.
 */
export const ReferencePdfViewerHost: React.FC = () => {
  const { s3 } = useServices();
  const openRequest = useAtomValue(referencePdfOpenRequestAtom);
  const setStatus = useSetAtom(referencePdfViewerStatusAtom);
  const [active, setActive] = useState<ActiveViewer | null>(null);
  const [loadingFileName, setLoadingFileName] = useState<string | null>(null);
  // The most recently handled request, compared by object identity so each
  // click (a fresh object) opens exactly once and an in-flight open can bail
  // when superseded. Seeded with the atom's value at mount so a request left
  // over from before a remount doesn't re-open the viewer.
  const handledRequestRef = useRef(openRequest);
  // Bumps per opened request so the viewer re-applies the page/search even when
  // the target repeats.
  const requestTokenRef = useRef(0);

  useEffect(() => {
    setStatus({
      activeFileName: active?.pdf.fileName ?? null,
      loadingFileName,
    });
  }, [active?.pdf.fileName, loadingFileName, setStatus]);

  const ensureBlob = useCallback(
    async (fileName: string): Promise<Blob | null> => {
      if (!s3) return null;
      let promise = pdfPromises.get(fileName);
      if (!promise) {
        promise = (async () => {
          const result = await s3.downloadFile(
            getConfig().RESOURCES_BUCKET_NAME,
            fileName,
            {
              onProgress: () => {
                /* no-op */
              },
            },
          );
          if (result.error) throw result.error;
          return result.data ?? null;
        })();
        pdfPromises.set(fileName, promise);
        promise.then(
          (blob) => {
            if (!blob) pdfPromises.delete(fileName);
          },
          () => {
            pdfPromises.delete(fileName);
          },
        );
      }
      return promise;
    },
    [s3],
  );

  const activeSearchFilename = active?.pdf.searchFilename;
  const handleSearch = useCallback(
    (query: string): Promise<PDFSearchResult> =>
      activeSearchFilename
        ? searchGeneralDocsByFilename(query, activeSearchFilename)
        : Promise.resolve({ matches: [] }),
    [activeSearchFilename],
  );

  const handleClose = useCallback(() => setActive(null), []);

  useEffect(() => {
    if (!openRequest) return;
    // Wait for the auth-backed S3 client before consuming the request: marking
    // it handled now and then failing on a transient `!s3` would burn the click
    // permanently (the effect re-runs when `s3` arrives, but the dedup ref would
    // already match). Bailing here keeps the request pending until `s3` is ready.
    if (!s3) return;
    if (openRequest === handledRequestRef.current) return;
    handledRequestRef.current = openRequest;
    const request = openRequest;
    const { pdf } = request;
    const target = deriveReferencePdfOpenState(request);
    const isCurrent = () => handledRequestRef.current === request;
    // `found` answers "does the reference exist"; the caller owns that feedback
    // when it passes `onResult`. Download failures always toast.
    const reportNotFound = () => {
      if (request.onResult) request.onResult({ found: false });
      else toast.error(`Couldn't find that reference in the ${pdf.title}.`);
    };
    const reportOpenFailed = () => {
      request.onResult?.({ found: true });
      toast.error(`Couldn't open the ${pdf.title}. Please try again.`);
    };

    void (async () => {
      try {
        let page = target.page;
        // Unmapped Table/Figure refs carry only a query — resolve the page via
        // BM25 before opening so a zero-match reference doesn't pop a blank viewer.
        if (target.needsSearch && target.query) {
          if (!pdf.searchFilename) {
            reportNotFound();
            return;
          }
          const { matches } = await searchGeneralDocsByFilename(
            target.query,
            pdf.searchFilename,
          );
          if (!isCurrent()) return;
          if (matches.length === 0) {
            reportNotFound();
            return;
          }
          page = matches[0].page;
        }

        setLoadingFileName(pdf.fileName);
        let blob: Blob | null;
        try {
          blob = await ensureBlob(pdf.fileName);
        } finally {
          setLoadingFileName((current) =>
            current === pdf.fileName ? null : current,
          );
        }
        if (!isCurrent()) return;
        if (!blob) {
          reportOpenFailed();
          return;
        }

        requestTokenRef.current += 1;
        setActive({
          pdf,
          blob,
          initialPage: page ?? 1,
          initialSearchQuery: target.query,
          initialSearchText: target.searchText,
          initialHighlightTerms: target.highlightTerms,
          requestToken: requestTokenRef.current,
          anchorRef: request.anchorRef,
        });
        request.onResult?.({ found: true });
      } catch (error) {
        if (!isCurrent()) return;
        console.error(`Failed to open ${pdf.fileName}:`, error);
        reportOpenFailed();
      }
    })();
  }, [openRequest, s3, ensureBlob]);

  if (!active) return null;

  return (
    <ReferencePdfFloatingViewer
      title={active.pdf.title}
      file={active.blob}
      initialPage={active.initialPage}
      initialSearchQuery={active.initialSearchQuery}
      initialSearchText={active.initialSearchText}
      initialHighlightTerms={active.initialHighlightTerms}
      requestToken={active.requestToken}
      onSearch={active.pdf.searchFilename ? handleSearch : undefined}
      onClose={handleClose}
      anchorRef={active.anchorRef}
    />
  );
};
